import config from 'config';
import memoize from 'memoize-one';
import * as constants from '~/utils/ang/constants';
import { getDefaultSort, getSortingOptions } from '~/utils/ang/sorting';
import { getAPIUrl } from '~/utils/api';

let isCategoriesUpdateFilterEnabled = false;
let isTagsUpdateFilterEnabled = false;

const getAssetTypesFilter = ({
  assetTypesConfiguration,
  featureFlags,
  types
}) =>
  Object.keys(assetTypesConfiguration).reduce((accum, typeKey) => {
    if (!types.some((type) => type === typeKey)) {
      return accum;
    }

    if (typeKey === 'default') {
      return accum;
    }

    if (accum.some((item) => item === typeKey)) {
      return accum;
    }

    const type = assetTypesConfiguration[typeKey];

    if (!type.filter) {
      return accum;
    }

    if (type.featureFlag && !featureFlags[type.featureFlag]) {
      return accum;
    }

    const typeItem = {
      k: type.angType,
      l: type.filter
    };

    if (accum.some((item) => item.l === typeItem.l && item.k === typeItem.k)) {
      return accum;
    }

    return [...accum, typeItem];
  }, []);

const getCategoriesFilter = ({ categories }) =>
  categories.map(({ displayName, tagKey, value }) => ({
    g: displayName,
    k: `${tagKey}:${value}`,
    l: value
  }));

const getOrganizationsFilter = ({ organizations }) =>
  organizations.map((organization) => {
    let organizationName;

    if (organization.id === config.mulesoft.id) {
      organizationName = `Provided by ${config.mulesoft.name}`;
    } else if (organization.isMaster) {
      organizationName = `${organization.name} (root)`;
    } else {
      organizationName = organization.name;
    }

    return {
      l: organizationName,
      g: organization.group,
      k: organization.id
    };
  });

const getTagsFilter = ({ tags }) =>
  tags.map((tag) => ({
    l: tag,
    k: tag
  }));

const getStatusLabel = (status) => {
  const label = status === 'published' ? 'stable' : status;

  return label[0].toUpperCase() + label.substring(1);
};

const getStatusesFilter = ({ statuses }) =>
  statuses.map((status) => ({
    l: getStatusLabel(status),
    k: status
  }));

const getGuiBoost = (boostConfiguration) =>
  [
    {
      'gui:target': 'assets.AssetNode.name',
      'gui:value': 4
    },
    {
      'gui:target': 'assets.AssetNode.description',
      'gui:value': 1.5
    },
    {
      'gui:target': 'customer-metadata.CustomerMetadataNode.categories',
      'gui:value': 1.4
    },
    {
      'gui:target': 'customer-metadata.CustomerMetadataNode.tags',
      'gui:value': 1.4
    }
  ].concat(
    boostConfiguration
      .filter((boost) => boost)
      .map((boost) => {
        const [target, value] = boost.split('^');

        return {
          'gui:target': target,
          'gui:value': parseFloat(value)
        };
      })
  );

const getConfig = ({
  context,
  facetsHasBeenRefreshed = false,
  updateFilter,
  assetTypesConfiguration,
  categories = [],
  organizations = [],
  tags = [],
  types = [],
  statuses = [],
  featureFlags,
  includeEmptyFilters = false,
  boostConfiguration = [],
  isPortalReArchitectureEnabled = false,
  isAuthenticated,
  sort,
  show,
  search
}) => {
  const assetTypesFilter = getAssetTypesFilter({
    assetTypesConfiguration,
    featureFlags,
    types
  });
  const categoriesFilter = getCategoriesFilter({ categories });
  const organizationsFilter = getOrganizationsFilter({ organizations });
  const tagsFilter = getTagsFilter({ tags });
  const statusFilter = getStatusesFilter({ statuses });

  if (facetsHasBeenRefreshed) {
    isCategoriesUpdateFilterEnabled =
      categoriesFilter.length >= constants.MAX_ANG_FACETS_RESULTS;
    isTagsUpdateFilterEnabled =
      tagsFilter.length >= constants.MAX_ANG_FACETS_RESULTS;
  }

  const categoriesUpdateFilter = isCategoriesUpdateFilterEnabled
    ? updateFilter
    : null;
  const tagsUpdateFilter = isTagsUpdateFilterEnabled ? updateFilter : null;
  const hasCategoriesSearchFunction = !!categoriesUpdateFilter;
  const hasTagsSearchFunction = !!tagsUpdateFilter;
  const sortingOptions = getSortingOptions({
    isAuthenticated,
    show,
    search
  });

  const sortingOptionToANGSortComponent = (sortingOption) => {
    return sortingOptions[sortingOption].fields
      ? {
          'gui:sort': {
            'gui:variable': '_sort',
            'gui:ordering': sortingOptions[sortingOption].fields.map(
              (field) => ({
                'gui:field': field.angField,
                'gui:order': field.order
              })
            )
          }
        }
      : {};
  };

  const defaultSort = getDefaultSort({
    isAuthenticated,
    show
  });
  const sortComponent = sortingOptionToANGSortComponent(
    sort && Object.keys(sortingOptions).includes(sort) ? sort : defaultSort
  );

  return {
    '@context': {
      gui: 'http://anypoint.com/vocabs/ang-gui#',
      xsd: 'http://www.w3.org/2001/XMLSchema#' // NOSONAR
    },
    '@type': 'gui:SearchConfiguration',
    'gui:size': {
      'gui:variable': '_size',
      'gui:value': constants.MAX_ANG_STREAM_SEARCH_RESULTS
    },
    'gui:container': {
      '@type': 'gui:ContainerNode',
      'gui:target': getAPIUrl({ context, pathname: '/api/v2/ang/stream' }),
      'gui:variable': 'q'
    },
    'gui:streamed': true,
    'gui:groups': [
      {
        '@type': 'gui:Group',
        'gui:label': 'Main',
        'gui:main': true,
        'gui:properties': [
          {
            '@type': 'gui:SearchableProperty',
            'gui:target': 'asset-minor-versions.VersionNode.generatedFrom',
            'gui:visible': false,
            'gui:range': {
              '@id': 'xsd:anyURI'
            },
            'gui:defaultValues': ['http://none']
          },
          ...(assetTypesFilter.length || includeEmptyFilters
            ? [
                {
                  '@type': 'gui:SearchableProperty',
                  'gui:target': 'assets.@type',
                  'gui:label': 'Asset Type',
                  'gui:main': true,
                  'gui:multi': true,
                  'gui:singular': 'type',
                  'gui:plural': 'types',
                  'gui:range': {
                    '@id': 'xsd:string'
                  },
                  'gui:values': assetTypesFilter
                }
              ]
            : []),
          ...(isAuthenticated &&
          (organizationsFilter.length || includeEmptyFilters)
            ? [
                {
                  '@type': 'gui:SearchableProperty',
                  'gui:target': 'assets.AssetNode.organizationId',
                  'gui:label': 'Organization',
                  'gui:main': true,
                  'gui:multi': true,
                  'gui:searchable': true,
                  'gui:singular': 'organization',
                  'gui:plural': 'organizations',
                  'gui:range': {
                    '@id': 'xsd:string'
                  },
                  'gui:values': organizationsFilter
                }
              ]
            : []),
          ...(categoriesFilter.length ||
          includeEmptyFilters ||
          hasCategoriesSearchFunction
            ? [
                {
                  '@type': 'gui:SearchableProperty',
                  'gui:target':
                    'customer-metadata.CustomerMetadataNode.categories',
                  'gui:label': isAuthenticated
                    ? 'Category'
                    : 'Category/Industry',
                  'gui:condition': 'AND',
                  'gui:main': true,
                  'gui:multi': true,
                  'gui:searchable': true,
                  'gui:searchFunction': categoriesUpdateFilter,
                  'gui:searchFunctionDelay': constants.SEARCH_FUNCTION_DELAY,
                  'gui:singular': isAuthenticated
                    ? 'category'
                    : 'category/industry',
                  'gui:plural': isAuthenticated
                    ? 'categories'
                    : 'categories/industries',
                  'gui:range': {
                    '@id': 'xsd:string'
                  },
                  'gui:values': categoriesFilter
                }
              ]
            : []),
          ...(tagsFilter.length || includeEmptyFilters || hasTagsSearchFunction
            ? [
                {
                  '@type': 'gui:SearchableProperty',
                  'gui:target': 'customer-metadata.CustomerMetadataNode.tags',
                  'gui:label': 'Tags',
                  'gui:condition': 'AND',
                  'gui:main': true,
                  'gui:multi': true,
                  'gui:searchable': true,
                  'gui:searchFunction': tagsUpdateFilter,
                  'gui:searchFunctionDelay': constants.SEARCH_FUNCTION_DELAY,
                  'gui:singular': 'tag',
                  'gui:plural': 'tags',
                  'gui:range': {
                    '@id': 'xsd:string'
                  },
                  'gui:values': tagsFilter
                }
              ]
            : []),
          {
            '@type': 'gui:SearchableProperty',
            'gui:target': `${
              isPortalReArchitectureEnabled ? '' : 'k:'
            }asset-minor-versions.VersionNode.status`,
            'gui:label': 'Lifecycle state',
            'gui:visible': isPortalReArchitectureEnabled,
            'gui:defaultValues': ['published', 'development', 'deprecated'],
            'gui:condition': 'OR',
            'gui:main': true,
            'gui:multi': true,
            'gui:searchable': false,
            'gui:searchFunction': null,
            'gui:searchFunctionDelay': null,
            'gui:singular': 'Lifecycle state',
            'gui:plural': 'Lifecycle states',
            'gui:range': {
              '@id': isPortalReArchitectureEnabled ? 'xsd:string' : 'xsd:anyURI'
            },
            'gui:values': statusFilter
          },
          ...(isAuthenticated
            ? [
                {
                  '@type': 'gui:SearchableProperty',
                  'gui:target': 'sharedWithMe',
                  'gui:visible': false,
                  'gui:range': {
                    '@id': 'xsd:bool'
                  },
                  'gui:defaultValues': []
                }
              ]
            : [])
        ]
      },
      {
        '@type': 'gui:Group',
        'gui:label': 'Asset property filters',
        'gui:properties': [
          {
            '@type': 'gui:SearchableProperty',
            'gui:target': 'assets.MinorAssetNode.updatedAt',
            'gui:label': 'Last modified',
            'gui:range': {
              '@id': 'xsd:date'
            }
          },
          {
            '@type': 'gui:CustomSearchableProperty',
            'gui:label': 'Deprecated versions',
            'gui:range': {
              '@id': 'deprecated'
            }
          }
        ]
      }
    ],
    'gui:boost': getGuiBoost(boostConfiguration),
    'gui:source': {
      'gui:variable': '_source',
      'gui:fields': [
        'assets.AssetNode.*',
        'assets.FileNode.*',
        'assets.@type',
        'assets.@id',
        'asset-major-versions.AssetMajorNode.*',
        'asset-minor-versions.MinorVersionNode.*',
        'asset-minor-versions.@id',
        'asset-minor-versions.VersionNode.*',
        'customer-metadata.CategoryNode.*',
        'customer-metadata.CustomerMetadataNode.*',
        'system-metadata.SystemMetadataNode.*'
      ]
    },
    ...(sortComponent || {})
  };
};

export default memoize(getConfig);
